import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, SpinnerGap, CaretDown, CaretUp, Clock, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ReviewPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [noteFields, setNoteFields] = useState({});
  const [showNote, setShowNote] = useState({});

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const resp = await fetch(`${API}/api/review/${token}`);
        if (resp.status === 404) { setError("not_found"); setLoading(false); return; }
        if (resp.status === 410) {
          const d = await resp.json();
          setError(d.detail?.includes("expired") ? "expired" : "deactivated");
          setLoading(false);
          return;
        }
        if (!resp.ok) { setError("error"); setLoading(false); return; }
        const d = await resp.json();
        setData(d);
        // Pre-populate existing decisions
        if (d.approvals) {
          const existing = {};
          Object.entries(d.approvals).forEach(([hid, a]) => {
            existing[hid] = { decision: a.decision, note: a.client_note, decidedAt: a.decided_at };
          });
          setDecisions(existing);
        }
      } catch { setError("error"); }
      setLoading(false);
    };
    fetchReview();
  }, [token]);

  const handleDecision = async (historyItemId, decision) => {
    setShowNote(prev => ({ ...prev, [historyItemId]: decision }));
  };

  const confirmDecision = async (historyItemId) => {
    const decision = showNote[historyItemId];
    if (!decision) return;
    setSubmitting(prev => ({ ...prev, [historyItemId]: true }));
    try {
      const resp = await fetch(`${API}/api/review/${token}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          history_item_id: historyItemId,
          decision,
          client_note: noteFields[historyItemId] || null,
        }),
      });
      if (resp.ok) {
        setDecisions(prev => ({
          ...prev,
          [historyItemId]: {
            decision,
            note: noteFields[historyItemId] || null,
            decidedAt: new Date().toISOString(),
          },
        }));
        setShowNote(prev => ({ ...prev, [historyItemId]: null }));
      }
    } catch { /* non-critical */ }
    setSubmitting(prev => ({ ...prev, [historyItemId]: false }));
  };

  const allApproved = data?.items?.length > 0 && data.items.every(
    item => decisions[item.history_id]?.decision === "approved"
  );

  // Error / expired states
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <SpinnerGap size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="review-error-state">
          <Warning size={48} className="mx-auto text-gray-300 mb-4" />
          {error === "not_found" && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Review link not found</h1>
              <p className="text-sm text-gray-500">This link may have been removed. Please contact the sender for an updated link.</p>
            </>
          )}
          {(error === "expired" || error === "deactivated") && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">This review link has {error === "expired" ? "expired" : "been deactivated"}</h1>
              <p className="text-sm text-gray-500">
                {data?.business_name ? `Please contact ${data.business_name}` : "Please contact the sender"} for an updated link.
              </p>
            </>
          )}
          {error === "error" && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
              <p className="text-sm text-gray-500">Please try refreshing the page or contact the sender.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* All approved banner */}
      <AnimatePresence>
        {allApproved && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="bg-emerald-500 text-white text-center py-3 px-4" data-testid="all-approved-banner">
            <p className="text-sm font-medium">All content approved — {data.business_name} has been notified.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-5">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1" data-testid="review-business-name">{data.business_name}</p>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="review-title">{data.title}</h1>
        </div>
      </header>

      {/* Message to client */}
      {data.message_to_client && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3" data-testid="review-message">
            <p className="text-sm text-blue-800 leading-relaxed whitespace-pre-wrap">{data.message_to_client}</p>
          </div>
        </div>
      )}

      {/* Content items */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {data.items.map((item, idx) => {
          const d = decisions[item.history_id];
          const currentNote = showNote[item.history_id];
          return (
            <ContentCard
              key={item.history_id || idx}
              item={item}
              decision={d}
              currentNote={currentNote}
              noteValue={noteFields[item.history_id] || ""}
              onNoteChange={(val) => setNoteFields(prev => ({ ...prev, [item.history_id]: val }))}
              onDecision={(dec) => handleDecision(item.history_id, dec)}
              onConfirm={() => confirmDecision(item.history_id)}
              onCancel={() => setShowNote(prev => ({ ...prev, [item.history_id]: null }))}
              submitting={submitting[item.history_id]}
              testIdx={idx}
            />
          );
        })}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 mt-12">
        <p className="text-center text-xs text-gray-400">Powered by Quibn</p>
      </footer>
    </div>
  );
}

function ContentCard({ item, decision, currentNote, noteValue, onNoteChange, onDecision, onConfirm, onCancel, submitting, testIdx }) {
  const [expanded, setExpanded] = useState(true);
  const hasDecision = !!decision;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: testIdx * 0.05 }} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid={`review-card-${testIdx}`}>
      {/* Card header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{item.content_type}</span>
            {item.platform && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{item.platform}</span>
              </>
            )}
          </div>
          <h2 className="text-base font-semibold text-gray-900" data-testid={`review-card-title-${testIdx}`}>{item.title || item.topic}</h2>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-testid={`review-toggle-${testIdx}`}>
          {expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </button>
      </div>

      {/* Content body */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-5 py-4 space-y-3" data-testid={`review-content-${testIdx}`}>
              {item.generated_core_angle && (
                <div className="bg-gray-50 rounded-lg px-4 py-2.5">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Angle</span>
                  <p className="text-sm text-gray-700 font-medium">{item.generated_core_angle}</p>
                </div>
              )}
              <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">
                {item.generated_draft}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Approval controls */}
      <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
        {hasDecision && !currentNote ? (
          <div className="space-y-2" data-testid={`review-decided-${testIdx}`}>
            <div className={`flex items-center gap-2 ${decision.decision === "approved" ? "text-emerald-600" : "text-amber-600"}`}>
              {decision.decision === "approved" ? <Check size={18} weight="bold" /> : <Clock size={18} />}
              <span className="text-sm font-medium">
                {decision.decision === "approved" ? `Approved` : "Changes requested"}
                {decision.decidedAt && ` — ${formatDate(decision.decidedAt)}`}
              </span>
            </div>
            {decision.note && <p className="text-xs text-gray-500 italic ml-7">"{decision.note}"</p>}
            <button onClick={() => onDecision(decision.decision === "approved" ? "changes_requested" : "approved")} className="text-xs text-gray-400 hover:text-gray-600 transition-colors ml-7" data-testid={`review-change-decision-${testIdx}`}>
              Change decision
            </button>
          </div>
        ) : currentNote ? (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid={`review-note-form-${testIdx}`}>
            <p className="text-xs text-gray-500 font-medium">Add a note (optional)</p>
            <textarea
              value={noteValue}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Any feedback or context for the team..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y min-h-[60px]"
              data-testid={`review-note-textarea-${testIdx}`}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={onConfirm}
                disabled={submitting}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors min-h-[44px] ${currentNote === "approved" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-amber-500 hover:bg-amber-600"} disabled:opacity-50`}
                data-testid={`review-confirm-${testIdx}`}
              >
                {submitting ? <SpinnerGap size={16} className="animate-spin" /> : "Confirm"}
              </button>
              <button onClick={onCancel} className="px-4 py-2.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid={`review-cancel-${testIdx}`}>
                Cancel
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2" data-testid={`review-buttons-${testIdx}`}>
            <button
              onClick={() => onDecision("approved")}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors min-h-[48px]"
              data-testid={`approve-btn-${testIdx}`}
            >
              <Check size={18} weight="bold" /> Approve This
            </button>
            <button
              onClick={() => onDecision("changes_requested")}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-amber-400 text-amber-600 hover:bg-amber-50 text-sm font-semibold transition-colors min-h-[48px]"
              data-testid={`request-changes-btn-${testIdx}`}
            >
              <X size={18} weight="bold" /> Request Changes
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
